import { Check, FileText, Plus, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "@/components/common/loader";
import { MemberMultiSelect } from "@/features/clients/components/member-multi-select";
import { openDocument } from "@/features/documents/api";
import { cn } from "@/lib/utils";
import {
  ACTION_LABELS,
  fetchRequiredDocDefaults,
  getOnboarding,
  type NewArrival,
  type OnboardingAction,
  type OnboardingState,
  PROGRESS_STAGES,
  type RequiredDocInput,
  STAGE_LABELS,
  submitProformaForApproval,
  type TransitionBody,
  transitionOnboarding,
} from "@/features/new-arrivals/api";

interface ManageOnboardingDialogProps {
  arrival: NewArrival;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after any successful transition so the queue can refetch. */
  onTransitioned: () => void;
}

const timeFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatWhen(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : timeFormatter.format(date);
}

/** Humanize an event's action string (e.g. "submit_for_approval" → "Submit for
 * approval") — falls back to the raw key for actions we don't label. */
function actionLabel(action: string): string {
  return ACTION_LABELS[action as OnboardingAction] ?? action.replace(/_/g, " ");
}

/** Destructive-styled actions get the destructive button variant. */
const DESTRUCTIVE_ACTIONS = new Set<OnboardingAction>(["reject", "client_reject"]);

/**
 * Drives the onboarding pipeline for a single arrival: shows the current stage,
 * a linear progress indicator, the server-permitted actions as buttons, and the
 * event timeline. `reject` opens a nested required-reason step; `assign_team`
 * reveals a member multi-select. Every async handler is guarded against
 * setState-after-unmount via a mounted ref.
 */
export function ManageOnboardingDialog({
  arrival,
  open,
  onOpenChange,
  onTransitioned,
}: ManageOnboardingDialogProps) {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<OnboardingAction | null>(null);

  // Reject sub-flow (shared by the SA `reject` and the on-behalf `client_reject`).
  const [rejecting, setRejecting] = useState(false);
  const [rejectAction, setRejectAction] = useState<OnboardingAction>("reject");
  const [rejectReason, setRejectReason] = useState("");

  // Prepare-&-send-PI sub-flow — the CD uploads the Proforma Invoice PDF.
  const [submittingPi, setSubmittingPi] = useState(false);
  const [piFile, setPiFile] = useState<File | null>(null);
  const [piError, setPiError] = useState<string | null>(null);

  // Assign-team sub-flow.
  const [assigning, setAssigning] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  // "Hand off to NSI Capitals" toggle inside the assign-team sub-flow.
  const [handOffToNsi, setHandOffToNsi] = useState(false);

  // Assign-NSI-member sub-flow (house Country Director picks the NSI member).
  const [assigningNsi, setAssigningNsi] = useState(false);
  const [selectedNsiIds, setSelectedNsiIds] = useState<string[]>([]);

  // Request-docs sub-flow — an editable checklist pre-filled from the firm's
  // per-client-type defaults.
  const [requestingDocs, setRequestingDocs] = useState(false);
  const [docRows, setDocRows] = useState<RequiredDocInput[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);

  const resetSubFlows = useCallback(() => {
    setRejecting(false);
    setRejectReason("");
    setAssigning(false);
    setSelectedMemberIds([]);
    setHandOffToNsi(false);
    setAssigningNsi(false);
    setSelectedNsiIds([]);
    setRequestingDocs(false);
    setDocRows([]);
    setSubmittingPi(false);
    setPiFile(null);
    setPiError(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await getOnboarding(arrival.id);
      if (!mountedRef.current) return;
      setState(next);
    } catch {
      if (!mountedRef.current) return;
      setError("Couldn't load onboarding details.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [arrival.id]);

  // Fetch (and reset the sub-flows) whenever the dialog opens for an arrival.
  // The request is initiated inline so setState stays in the async callbacks
  // rather than running synchronously in the effect body.
  useEffect(() => {
    if (!open) return;
    resetSubFlows();
    setLoading(true);
    setError(null);
    getOnboarding(arrival.id)
      .then((next) => {
        if (mountedRef.current) setState(next);
      })
      .catch(() => {
        if (mountedRef.current) setError("Couldn't load onboarding details.");
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [open, arrival.id, resetSubFlows]);

  async function runTransition(
    action: OnboardingAction,
    extra: Omit<TransitionBody, "action"> = {},
  ) {
    setSubmitting(action);
    try {
      await transitionOnboarding(arrival.id, { action, ...extra });
      if (!mountedRef.current) return;
      toast.success(`${ACTION_LABELS[action]} done.`);
      resetSubFlows();
      onTransitioned();
      await load();
    } catch (err) {
      if (!mountedRef.current) return;
      toast.error(err instanceof Error ? err.message : "Action failed.");
    } finally {
      if (mountedRef.current) setSubmitting(null);
    }
  }

  async function submitPi() {
    if (!piFile) {
      setPiError("Choose a PDF to upload.");
      return;
    }
    setSubmitting("submit_for_approval");
    try {
      await submitProformaForApproval(arrival.id, piFile);
      if (!mountedRef.current) return;
      toast.success("Proforma Invoice sent for approval.");
      resetSubFlows();
      onTransitioned();
      await load();
    } catch (err) {
      if (!mountedRef.current) return;
      toast.error(err instanceof Error ? err.message : "Action failed.");
    } finally {
      if (mountedRef.current) setSubmitting(null);
    }
  }

  function handleAction(action: OnboardingAction) {
    if (action === "submit_for_approval") {
      resetSubFlows();
      setSubmittingPi(true);
      return;
    }
    if (action === "reject" || action === "client_reject") {
      resetSubFlows();
      setRejectAction(action);
      setRejecting(true);
      return;
    }
    if (action === "assign_team") {
      // The assignable team (the client's own firm members) comes with the
      // onboarding state, so no extra fetch is needed.
      resetSubFlows();
      setAssigning(true);
      return;
    }
    if (action === "assign_nsi_member") {
      // House Country Director picks the NSI member(s); preselect any already
      // assigned so the picker reflects the current state.
      resetSubFlows();
      setSelectedNsiIds(state?.nsiMemberIds ?? []);
      setAssigningNsi(true);
      return;
    }
    if (action === "request_docs") {
      resetSubFlows();
      setRequestingDocs(true);
      // Pre-fill from the firm's per-client-type defaults; an empty list on
      // failure just starts the CD with a blank checklist.
      setDocsLoading(true);
      fetchRequiredDocDefaults(arrival.id)
        .then((rows) => mountedRef.current && setDocRows(rows))
        .catch(() => mountedRef.current && setDocRows([]))
        .finally(() => mountedRef.current && setDocsLoading(false));
      return;
    }
    void runTransition(action);
  }

  function submitRequestDocs() {
    const required_documents = docRows
      .map((r) => ({ name: r.name.trim(), notes: (r.notes ?? "").trim() }))
      .filter((r) => r.name);
    void runTransition("request_docs", { required_documents });
  }

  function submitReject() {
    // The reason is optional on both reject legs (SA and client-on-behalf).
    const reason = rejectReason.trim();
    void runTransition(rejectAction, reason ? { note: reason } : {});
  }

  function submitAssign() {
    void runTransition("assign_team", {
      assigned_member_ids: selectedMemberIds.map((id) => Number(id)),
      hand_off_to_nsi: handOffToNsi,
    });
  }

  function submitAssignNsi() {
    void runTransition("assign_nsi_member", {
      nsi_member_ids: selectedNsiIds.map((id) => Number(id)),
    });
  }

  const busy = submitting !== null;
  const currentStage = state?.stage ?? arrival.onboardingStage;
  const currentIndex = PROGRESS_STAGES.indexOf(currentStage);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{arrival.name}</DialogTitle>
          <DialogDescription>
            Manage this client's onboarding approval pipeline.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        ) : error ? (
          <p className="text-destructive py-6 text-sm">{error}</p>
        ) : state ? (
          <div className="flex flex-col gap-6">
            {/* Current stage + progress track */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Current stage</span>
                <Badge variant={currentStage === "REJECTED" ? "destructive" : "secondary"}>
                  {STAGE_LABELS[currentStage]}
                </Badge>
              </div>
              <ol className="flex flex-wrap items-center gap-1" aria-label="Onboarding progress">
                {PROGRESS_STAGES.map((stage, i) => {
                  const done = currentIndex >= 0 && i < currentIndex;
                  const active = currentIndex >= 0 && i === currentIndex;
                  return (
                    <li key={stage} className="flex items-center gap-1">
                      <span
                        className={cn(
                          "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                          active
                            ? "bg-primary text-primary-foreground"
                            : done
                              ? "bg-primary/15 text-primary"
                              : "bg-muted text-muted-foreground",
                        )}
                      >
                        {done && <Check className="size-3" />}
                        {STAGE_LABELS[stage]}
                      </span>
                      {i < PROGRESS_STAGES.length - 1 && (
                        <span className="text-muted-foreground/40 text-xs">›</span>
                      )}
                    </li>
                  );
                })}
              </ol>
              {currentStage === "REJECTED" && state.rejectionReason && (
                <p className="text-destructive bg-destructive/10 rounded-md px-3 py-2 text-sm">
                  <span className="font-medium">Rejection reason:</span>{" "}
                  {state.rejectionReason}
                </p>
              )}
            </div>

            {/* The current Proforma Invoice, once uploaded — viewable by staff. */}
            {state.proforma && (
              <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
                <span className="flex items-center gap-2 text-sm">
                  <FileText className="text-muted-foreground size-4" />
                  Proforma Invoice
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => state.proforma && void openDocument(state.proforma.id)}
                >
                  View PI
                </Button>
              </div>
            )}

            {/* Actions */}
            {state.availableActions.length > 0 &&
              !rejecting &&
              !assigning &&
              !assigningNsi &&
              !requestingDocs &&
              !submittingPi && (
              <div className="flex flex-wrap gap-2">
                {state.availableActions.map((action) => (
                  <Button
                    key={action}
                    type="button"
                    variant={DESTRUCTIVE_ACTIONS.has(action) ? "destructive" : "default"}
                    disabled={busy}
                    onClick={() => handleAction(action)}
                  >
                    {submitting === action && <Loader className="size-4" />}
                    {ACTION_LABELS[action]}
                  </Button>
                ))}
              </div>
            )}

            {/* Reject sub-flow — optional reason (SA reject or client-on-behalf) */}
            {rejecting && (
              <div className="border-destructive/30 flex flex-col gap-3 rounded-md border p-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="reject-reason">
                    {rejectAction === "client_reject"
                      ? "Client's reason (optional)"
                      : "Reason (optional)"}
                  </Label>
                  <Textarea
                    id="reject-reason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Explain what needs rework before resubmission…"
                    disabled={busy}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setRejecting(false);
                      setRejectReason("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={busy}
                    onClick={submitReject}
                  >
                    {submitting === rejectAction && <Loader className="size-4" />}
                    {ACTION_LABELS[rejectAction]}
                  </Button>
                </div>
              </div>
            )}

            {/* Prepare-&-send-PI sub-flow — upload the Proforma Invoice PDF. */}
            {submittingPi && (
              <div className="flex flex-col gap-3 rounded-md border p-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="pi-file">Proforma Invoice (PDF)</Label>
                  <Input
                    id="pi-file"
                    type="file"
                    accept="application/pdf,.pdf"
                    disabled={busy}
                    onChange={(e) => {
                      setPiFile(e.target.files?.[0] ?? null);
                      if (piError) setPiError(null);
                    }}
                  />
                  <p className="text-muted-foreground text-xs">
                    Sent to the Super Admin for approval, then shared with the client.
                  </p>
                  {piError && <p className="text-destructive text-sm">{piError}</p>}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setSubmittingPi(false);
                      setPiFile(null);
                      setPiError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="button" disabled={busy} onClick={() => void submitPi()}>
                    {submitting === "submit_for_approval" && <Loader className="size-4" />}
                    Send for approval
                  </Button>
                </div>
              </div>
            )}

            {/* Assign-team sub-flow — member multi-select */}
            {assigning && (
              <div className="flex flex-col gap-3 rounded-md border p-4">
                <div className="flex flex-col gap-1.5">
                  <Label>Assign team members</Label>
                  <MemberMultiSelect
                    members={(state?.assignableMembers ?? []).map((m) => ({
                      id: m.id,
                      name: m.name,
                      email: "",
                    }))}
                    selected={selectedMemberIds}
                    onChange={setSelectedMemberIds}
                    disabled={busy}
                    placeholder="Team members"
                    triggerSize="default"
                  />
                </div>
                {/* NSI handoff — the BP's Country Director can hand this client
                    to NSI Capitals; the NSI Country Director then picks the
                    actual NSI team member. Shown only for a BP client. */}
                {(state?.nsiCountryDirector?.length ?? 0) > 0 && (
                  <label className="flex items-start gap-2.5 rounded-md border p-3 text-sm">
                    <Checkbox
                      aria-label="Hand off to NSI Capitals"
                      checked={handOffToNsi}
                      onCheckedChange={(next) => setHandOffToNsi(next === true)}
                      disabled={busy}
                      className="mt-0.5"
                    />
                    <span className="grid gap-0.5">
                      <span className="font-medium">Hand off to NSI Capitals</span>
                      <span className="text-muted-foreground text-xs">
                        NSI Country Director (
                        {state?.nsiCountryDirector?.map((d) => d.name).join(", ")}) will
                        assign the NSI team member.
                      </span>
                    </span>
                  </label>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setAssigning(false);
                      setSelectedMemberIds([]);
                      setHandOffToNsi(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="button" disabled={busy} onClick={submitAssign}>
                    {submitting === "assign_team" && <Loader className="size-4" />}
                    Assign team
                  </Button>
                </div>
              </div>
            )}

            {/* Assign-NSI-member sub-flow — house Country Director picks the NSI
                team member(s) for a client the BP handed to NSI Capitals. */}
            {assigningNsi && (
              <div className="flex flex-col gap-3 rounded-md border p-4">
                <div className="flex flex-col gap-1.5">
                  <Label>Assign NSI team member</Label>
                  <MemberMultiSelect
                    members={(state?.nsiAssignableMembers ?? []).map((m) => ({
                      id: m.id,
                      name: m.name,
                      email: "",
                    }))}
                    selected={selectedNsiIds}
                    onChange={setSelectedNsiIds}
                    disabled={busy}
                    placeholder="NSI members"
                    triggerSize="default"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setAssigningNsi(false);
                      setSelectedNsiIds([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="button" disabled={busy} onClick={submitAssignNsi}>
                    {submitting === "assign_nsi_member" && <Loader className="size-4" />}
                    Assign NSI member
                  </Button>
                </div>
              </div>
            )}

            {/* Request-docs sub-flow — editable checklist pre-filled from the
                firm's per-client-type defaults. */}
            {requestingDocs && (
              <div className="flex flex-col gap-3 rounded-md border p-4">
                <Label>Documents to request</Label>
                {docsLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader />
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {docRows.length === 0 && (
                      <p className="text-muted-foreground text-sm">
                        No documents yet — add the ones this client should provide.
                      </p>
                    )}
                    {docRows.map((row, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="grid flex-1 gap-2 sm:grid-cols-2">
                          <Input
                            aria-label={`Document ${i + 1} name`}
                            value={row.name}
                            placeholder="Document name"
                            disabled={busy}
                            onChange={(e) =>
                              setDocRows((rows) =>
                                rows.map((r, j) =>
                                  j === i ? { ...r, name: e.target.value } : r,
                                ),
                              )
                            }
                          />
                          <Input
                            aria-label={`Document ${i + 1} notes`}
                            value={row.notes ?? ""}
                            placeholder="Notes (optional)"
                            disabled={busy}
                            onChange={(e) =>
                              setDocRows((rows) =>
                                rows.map((r, j) =>
                                  j === i ? { ...r, notes: e.target.value } : r,
                                ),
                              )
                            }
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove document ${i + 1}`}
                          disabled={busy}
                          onClick={() => setDocRows((rows) => rows.filter((_, j) => j !== i))}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    ))}
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => setDocRows((rows) => [...rows, { name: "", notes: "" }])}
                      >
                        <Plus className="size-4" /> Add document
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setRequestingDocs(false);
                      setDocRows([]);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="button" disabled={busy} onClick={submitRequestDocs}>
                    {submitting === "request_docs" && <Loader className="size-4" />}
                    Request documents
                  </Button>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="flex flex-col gap-3">
              <h4 className="text-foreground text-sm font-semibold">Activity</h4>
              {state.events.length === 0 ? (
                <p className="text-muted-foreground text-sm">No activity recorded yet.</p>
              ) : (
                <ol className="flex flex-col gap-3">
                  {state.events.map((event) => (
                    <li key={event.id} className="flex flex-col gap-0.5 border-l-2 pl-3">
                      <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                        <span className="font-medium">{event.actorName || "System"}</span>
                        <span className="text-muted-foreground">{actionLabel(event.action)}</span>
                        <span className="text-muted-foreground ml-auto text-xs">
                          {formatWhen(event.createdAt)}
                        </span>
                      </div>
                      {event.note && (
                        <p className="text-muted-foreground text-sm">{event.note}</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
